import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams, useNavigate, useLocation, Routes, Route } from 'react-router-dom-v5-compat';
import { useTable, useBlockLayout } from 'react-table';
import { useDispatch } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { ActionablePlaceholder } from 'ui-components/components/display-grid/actionable-placeholder';
import { Button } from 'ui-components/components/button';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { LogService } from '../../../services/log-service';
import { FIRST_PAGE, PAGE_SIZE_250 } from '../../../services/pagination-service';

import { useQuery } from '../../../hooks/routing/use-query';
import { useModal } from '../../../hooks/use-modal';
import { V6RouteModalLoaderModalWrapper } from '../../../components/modals/shipment-details-modal/shipment-modal-loader';
import { DateRangeDropdown } from '../../../components/date-range-dropdown/index';
import { PagingControl } from '../../../components/paging-control/index';

import { getTransactionHistory, getTransactionDetails } from './api/index';
import TransactionDetailsModal from './transaction-details-modal';
import { exportTransactionHistoryReportRequest } from './transaction-history.actions';

import { walletBalanceHistoryColumns as columns } from './columns';
import messages from './messages';
import style from './style.module.less';

const title = <FormattedMessage {...messages.errorLoadingDetailsTitle} />;
const content = <FormattedMessage {...messages.errorLoadingDetailsContent} />;

const defaultColumn = {
  width: 175,
  minWidth: 75,
};

const allFilters = ['startDateTime', 'endDateTime'];

const TransactionHistoryGrid = () => {
  const { open, close, isOpen } = useModal();
  const [transactionGridResponse, setTransactionGridResponse] = useAsyncSafeState({});
  const [transactionData, setTransactionData] = useAsyncSafeState({});
  const [filterSorts, setFilterSorts] = useAsyncSafeState({});
  const { totalCount = 0, pageData = [] } = transactionGridResponse;
  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: pageData,
      defaultColumn,
    },
    // block layout and divs because that allows us to reorder and resize columns if we want to in the future
    useBlockLayout,
  );

  const location = useLocation();
  const navigate = useNavigate();
  const query = useQuery();
  const { walletId } = useParams();
  const page = parseFloat(query.get('page')) || FIRST_PAGE;
  const pageSize = parseFloat(query.get('pageSize')) || PAGE_SIZE_250;
  const offset = (page - 1) * pageSize;
  const ref = useRef(null);
  const dispatch = useDispatch();

  const onPaginationChange = useCallback(
    (_page, _pageSize) => {
      query.set('page', _page);
      query.set('pageSize', _pageSize);
      navigate(`?${query.toString()}`);
      const params = {
        pagination: { pageNumber: _page, pageSize: _pageSize },
        ...filterSorts,
      };
      getTransactionHistory(walletId, params)
        .then((historyData) => {
          if (ref.current) {
            ref.current.scrollTo(0, 0);
          }
          setTransactionGridResponse(historyData);
        })
        .catch((e) => LogService.logInfo(e));
    },
    [query, navigate, filterSorts, walletId, setTransactionGridResponse],
  );

  const resetPagination = useCallback(() => {
    navigate(location.url);
  }, [navigate, location]);

  useEffect(() => {
    if (!walletId) {
      return;
    }
    getTransactionHistory(walletId)
      .then((historyData) => setTransactionGridResponse(historyData))
      .catch((e) => LogService.logInfo(e));
  }, [walletId, setTransactionGridResponse]);

  const onFilterOrSortChange = useCallback(
    async (newFilterSorts) => {
      setFilterSorts(newFilterSorts);
      try {
        const historyData = await getTransactionHistory(walletId, newFilterSorts);
        resetPagination();
        setTransactionGridResponse(historyData);
      } catch (e) {
        LogService.logInfo(e);
      }
    },
    [resetPagination, setFilterSorts, setTransactionGridResponse, walletId],
  );

  const onDateRangeChange = useCallback(
    ({ startDate: startDateTime, endDate: endDateTime }) => {
      const newFilterSorts = { ...filterSorts, startDateTime, endDateTime };
      onFilterOrSortChange(newFilterSorts);
    },
    [filterSorts, onFilterOrSortChange],
  );

  const resetFilters = useCallback(() => {
    const newFilterSorts = Object.keys(filterSorts).reduce(
      (acc, key) => (allFilters.includes(key) ? acc : { ...acc, [key]: filterSorts[key] }),
      {},
    );
    onFilterOrSortChange(newFilterSorts);
  }, [filterSorts, onFilterOrSortChange]);

  const handleOnClickForExportLink = useCallback(() => {
    const params = {
      ...filterSorts,
    };
    dispatch(exportTransactionHistoryReportRequest(walletId, params));
  }, [dispatch, filterSorts, walletId]);

  const hasActiveFilters = useMemo(() => {
    return allFilters.some((filter) => filterSorts[filter]);
  }, [filterSorts]);

  const loadTransactionDetailsModal = useCallback(
    (transactionId) => {
      const _loadTransactionDetailsModal = async () => {
        try {
          const transactionDataResponse = await getTransactionDetails(walletId, transactionId);
          setTransactionData(transactionDataResponse);
          open();
        } catch {
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_ERROR,
            title,
            content,
          });
        }
      };
      return _loadTransactionDetailsModal();
    },
    [open, setTransactionData, walletId],
  );

  const { getHeaderGroupProps, headers } = headerGroups[0];

  return (
    <>
      <div className={style['controls-group']}>
        <div>
          <DateRangeDropdown
            dropdownTogglerClassname={style['date-range-toggler']}
            dropdownTogglerTheme='link'
            endDate={filterSorts?.endDateTime}
            onDateRangeUpdated={onDateRangeChange}
            startDate={filterSorts?.startDateTime}
          />
          {hasActiveFilters && (
            <Button onClick={resetFilters} theme='link'>
              <FormattedMessage {...messages.resetFilters} />
            </Button>
          )}
        </div>
        <Button className={style['button-link']} onClick={handleOnClickForExportLink} theme='link'>
          <FormattedMessage {...messages.exportLinkText} />
        </Button>
      </div>
      <div {...getTableProps()} ref={ref} className={style.table}>
        <div className={style['header-background']}>
          <div {...getHeaderGroupProps()}>
            {/* NOTE: The get* methods from react table provide a key prop to elements in arrays. */}
            {/* oxlint-disable-next-line react/jsx-key */}
            {headers.map(({ getHeaderProps, render }) => (
              <div {...getHeaderProps()} className={style['header-cell']}>
                {render('Header')}
                <div className={style['header-divider']} />
              </div>
            ))}
          </div>
        </div>
        <div {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const { getRowProps } = row;
            const { key } = getRowProps();
            return (
              <div key={key} className={style['row-background']}>
                <div className={style.row} {...getRowProps()}>
                  {/* NOTE: The get* methods from react table provide a key prop to elements in arrays. */}
                  {/* oxlint-disable-next-line react/jsx-key */}
                  {row.cells.map(({ getCellProps, render }) => (
                    <div {...getCellProps()} className={style.cell}>
                      {render('Cell', { loadTransactionDetailsModal })}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {!pageData.length && (
          <ActionablePlaceholder
            description={<FormattedMessage {...messages.placeholderDescription} />}
            title={<FormattedMessage {...messages.placeholderTitle} />}
          />
        )}
      </div>
      <PagingControl
        onPageChange={onPaginationChange}
        page={page}
        pageSize={pageSize}
        totalPages={Math.ceil(totalCount / pageSize)}
        totalRecords={totalCount}
        viewEndingCount={offset + Math.min(pageData.length, pageSize)}
        viewStartingCount={offset + 1}
      />
      <Routes>
        <Route element={<V6RouteModalLoaderModalWrapper />} path='/shipment/:fulfillmentPlanId' />
      </Routes>
      {isOpen && transactionData && <TransactionDetailsModal close={close} data={transactionData} />}
    </>
  );
};

export default TransactionHistoryGrid;
